import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
// @mui
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useState } from 'react';

// ----------------------------------------------------------------------

const filter = createFilterOptions();

export default function RHFAutocomplete({
  name,
  label,
  placeholder,
  helperText,
  enableAddOption = false,
  onAdd,
  allowFilterModes,
  filterMode = 'include',
  onChangeFilterMode = () => {},
  onChange,
  ...other
}) {
  const { control, setValue } = useFormContext();

  const { options } = other;
  const [selectAll, setSelectAll] = useState(false);
  const [, setTemp] = useState(false); // FORCES rerender to fetch new options

  const onSelectAllClick = () => {
    if (selectAll) {
      setValue(name, [], { shouldDirty: true });
      setSelectAll(false);
    } else {
      setValue(name, options, { shouldDirty: true });
      setSelectAll(true);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          // onChange={(event, newValue) => {
          //   setValue(name, newValue, { shouldValidate: true })
          // }}
          PaperComponent={(paperProps) => {
            const { children, ...restPaperProps } = paperProps;
            return (
              <Paper {...restPaperProps}>
                <Stack>
                  {allowFilterModes && (
                    <>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        p={1}
                      >
                        <FormControl>
                          <RadioGroup
                            row
                            value={filterMode ?? 'include'}
                            onChange={(e, value) => onChangeFilterMode(value)}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <FormControlLabel
                              value={'include'}
                              control={<Radio checked={filterMode === 'include'} />}
                              label={'Include'}
                              onClick={(e) => {
                                e.preventDefault();
                                onChangeFilterMode('include');
                              }}
                            />
                            <FormControlLabel
                              value={'exclude'}
                              control={<Radio checked={filterMode === 'exclude'} />}
                              label={'Exclude'}
                              onClick={(e) => {
                                e.preventDefault();
                                onChangeFilterMode('exclude');
                              }}
                            />
                          </RadioGroup>
                        </FormControl>
                      </Stack>
                      <Divider />
                    </>
                  )}

                  {other?.multiple && other?.options?.length > 1 && (
                    <>
                      <Box pl={1} py={0.5} mb={0.5} onMouseDown={(e) => e.preventDefault()}>
                        <FormControlLabel
                          onClick={(e) => {
                            e.preventDefault(); // prevent blur
                            onSelectAllClick();
                          }}
                          label="Select all"
                          control={<Checkbox id="select-all-checkbox" checked={selectAll} />}
                        />
                      </Box>
                      <Divider sx={{ mb: 0.5 }} />
                    </>
                  )}
                </Stack>

                {children}
              </Paper>
            );
          }}
          onChange={async (event, newValue) => {
            const latestValue = newValue?.length ? newValue[newValue.length - 1] : null;
            if (typeof newValue === 'string') {
              setValue(
                name,
                [
                  ...field.value,
                  {
                    label: newValue,
                  },
                ],
                { shouldValidate: true, shouldDirty: true }
              );
            } else if (newValue && latestValue?.inputValue) {
              // Create a new value from the user input
              const { data } = await onAdd(latestValue.inputValue);

              setValue(
                name,
                [
                  ...field.value,
                  {
                    label: latestValue.inputValue,
                    value: data?.row?.returning[0]?.group_id,
                  },
                ],
                { shouldValidate: true, shouldDirty: true }
              );
            } else {
              setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
            }
            onChange && onChange(newValue);
          }}
          onInputChange={(event, newInputValue) => {
            setTemp(newInputValue);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            if (enableAddOption) {
              const { inputValue } = params;
              const isExisting = options?.some((option) => inputValue === option.label);
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  label: `Add "${inputValue}"`,
                });
              }
            }

            return filtered;
          }}
          getOptionLabel={(option) => {
            // Value selected with enter, right from the input
            if (typeof option === 'string') {
              return option;
            }
            // Add "xxx" option created dynamically
            if (option.inputValue) {
              return option.inputValue;
            }
            // Regular option
            return option.label;
          }}
          renderOption={(props, option) => {
            const { key, ...restProps } = props;
            return (
              <li {...restProps} key={option.value || key}>
                {option.label}
              </li>
            );
          }}
          renderInput={(params) => (
            <Stack gap={0.5}>
              <TextField
                label={label}
                placeholder={placeholder}
                error={!!error}
                helperText={error ? error?.message : helperText}
                {...params}
                InputProps={{
                  ...(params.InputProps ?? {}),
                  startAdornment:
                    params.InputProps?.startAdornment?.length > 0
                      ? [
                          ...(filterMode === 'exclude'
                            ? [
                                <Chip
                                  size="small"
                                  variant={'outlined'}
                                  color={'primary'}
                                  label={'Exclude'}
                                  sx={{ mr: 1 }}
                                />,
                              ]
                            : []),
                          ...(params.InputProps?.startAdornment ?? []),
                        ]
                      : null,
                }}
              />
            </Stack>
          )}
          {...other}
        />
      )}
    />
  );
}

RHFAutocomplete.propTypes = {
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
};
